import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  signIn,
  confirmSignIn,
  fetchAuthSession,
  signOut,
  AuthSession,
  ConfirmSignInOutput,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';

/** Datos de sesión no secretos que lee el resto de la app (por pestaña). */
const SESSION_KEYS = ['userId', 'userEmail', 'userName', 'orgId'];
/** Lo que versiones anteriores dejaban en localStorage y ya no debe existir. */
const LEGACY_LOCAL_KEYS = ['accessToken', 'idToken', ...SESSION_KEYS];

/**
 * Servicio de autenticación (Cognito vía AWS Amplify v6).
 *
 * Antes de la migración, Medtronic era un *remote* de Module Federation y el
 * shell de Epione era quien autenticaba y dejaba el `idToken` en localStorage.
 * Ahora la app es autónoma, así que gestiona su propia sesión.
 *
 * Seguridad del almacenamiento en el navegador:
 *   · Los tokens de Cognito (incluido el refresh token) viven en
 *     sessionStorage, no en localStorage: se borran al cerrar la pestaña o el
 *     navegador y no quedan en disco para otro usuario del mismo equipo.
 *   · Los tokens NO se copian a ningún otro key: solo Amplify los guarda y el
 *     interceptor los pide con getIdToken().
 *   · En sessionStorage solo quedan datos no secretos para la interfaz
 *     (userId = sub, nombre, correo). La autorización la decide SIEMPRE el
 *     API con el token, nunca con estos valores.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.COGNITO_USERPOOL_ID,
          userPoolClientId: environment.COGNITO_APPCLIENT_ID,
        },
      },
    });
    cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
    this.purgeLegacyStorage();
  }

  /** Borra tokens y datos que versiones anteriores dejaron en localStorage. */
  private purgeLegacyStorage(): void {
    try {
      LEGACY_LOCAL_KEYS.forEach(k => localStorage.removeItem(k));
      Object.keys(localStorage)
        .filter(k => k.startsWith('CognitoIdentityServiceProvider.') || k.startsWith('amplify-'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* storage bloqueado: nada que limpiar */ }
  }

  private isNamedError(error: unknown): error is { name: string } {
    return typeof error === 'object' && error !== null && 'name' in error;
  }

  private hasMessage(error: unknown): error is { message: string } {
    return typeof error === 'object' && error !== null && 'message' in error;
  }

  public async logIn(
    username: string,
    password: string
  ): Promise<{ authState: 'loginSuccess' | 'newPasswordRequired' | 'mfaRequired' | 'emailCodeRequired' }> {
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });

      if (isSignedIn) {
        await this.setUserData();
        return { authState: 'loginSuccess' };
      }

      switch (nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          return { authState: 'newPasswordRequired' };
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          return { authState: 'mfaRequired' };
        case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
          return { authState: 'emailCodeRequired' };
        default:
          throw new Error(`Paso de inicio de sesión no soportado: ${nextStep.signInStep}`);
      }
    } catch (err) {
      console.error('AuthenticationService: error de Amplify en signIn:', err);
      let newErr = new Error('Algo salió mal al iniciar sesión.');
      if (this.isNamedError(err)) {
        if (err.name === 'UserNotFoundException') {
          newErr = new Error('El usuario ingresado no existe.');
        } else if (err.name === 'NotAuthorizedException' && this.hasMessage(err)) {
          if (err.message.includes('Temporary password has expired')) {
            newErr = new Error(
              'Tu contraseña temporal expiró. Contacta al administrador para que te genere una nueva.'
            );
          } else if (err.message.includes('Password attempts exceeded')) {
            newErr = new Error('Demasiados intentos fallidos. Espera unos minutos antes de intentar de nuevo.');
          } else {
            newErr = new Error('Contraseña o usuario incorrectos.');
          }
        }
      }
      throw newErr;
    }
  }

  public async sendVerificationCode(code: string): Promise<void> {
    try {
      await confirmSignIn({ challengeResponse: code });
      await this.setUserData();
    } catch (err) {
      console.error('AuthenticationService: error en confirmSignIn:', err);
      throw new Error(this.hasMessage(err) ? err.message : 'Código de verificación incorrecto.');
    }
  }

  public async confirmPassword(newPassword: string, name: string): Promise<ConfirmSignInOutput> {
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: { userAttributes: { name } },
      });
      if (result.isSignedIn) {
        await this.setUserData();
      }
      return result;
    } catch (err) {
      console.error('AuthenticationService: error en confirmPassword:', err);
      let newErr = new Error('Algo salió mal al confirmar los datos.');
      if (this.hasMessage(err)) {
        if (err.message.includes('Password not long enough')) newErr = new Error('La contraseña no es válida. Debe ser más larga.');
        else if (err.message.includes('uppercase')) newErr = new Error('La contraseña debe contener mayúsculas.');
        else if (err.message.includes('numeric')) newErr = new Error('La contraseña debe contener números.');
        else if (err.message.includes('symbol')) newErr = new Error('La contraseña debe contener símbolos.');
      }
      throw newErr;
    }
  }

  public async forgotPassword(username: string): Promise<void> {
    try {
      await resetPassword({ username });
    } catch (err) {
      console.error('AuthenticationService: error en resetPassword:', err);
      throw new Error(this.hasMessage(err) ? err.message : 'No se pudo iniciar el reseteo de contraseña.');
    }
  }

  public async confirmForgotPassword(
    username: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
    } catch (err) {
      console.error('AuthenticationService: error en confirmResetPassword:', err);
      throw new Error(this.hasMessage(err) ? err.message : 'No se pudo confirmar la nueva contraseña.');
    }
  }

  public async isLoggedIn(): Promise<boolean> {
    try {
      const session: AuthSession = await fetchAuthSession();
      const ok = session.tokens !== undefined;
      if (ok && !sessionStorage.getItem('userId')) await this.setUserData();
      return ok;
    } catch {
      return false;
    }
  }

  public async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.accessToken.toString() ?? null;
    } catch {
      return null;
    }
  }

  public async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  public getCurrentUserSub(): string | null {
    return sessionStorage.getItem('userId');
  }

  public getCurrentUserEmail(): string | null {
    return sessionStorage.getItem('userEmail');
  }

  public getOrgId(): string | null {
    return sessionStorage.getItem('orgId');
  }

  public async logOut(): Promise<void> {
    try {
      await signOut();
    } finally {
      SESSION_KEYS.forEach(k => sessionStorage.removeItem(k));
      this.purgeLegacyStorage();
    }
  }

  /**
   * Guarda en sessionStorage los datos NO secretos que usa la interfaz
   * (nunca los tokens):
   *   - userId    = sub (UUID estable, coincide con el id en la tabla users)
   *   - userEmail = email / cognito:username
   */
  private async setUserData(): Promise<void> {
    const session = await fetchAuthSession();
    if (session.tokens && session.tokens.idToken) {
      const payload = session.tokens.idToken.payload;

      sessionStorage.setItem('userId', payload['sub'] as string);
      sessionStorage.setItem(
        'userEmail',
        (payload['email'] as string) ?? (payload['cognito:username'] as string) ?? ''
      );
      if (payload['name']) {
        sessionStorage.setItem('userName', payload['name'] as string);
      }

      const orgId = (payload['custom:org_id'] ??
        payload['custom:organization'] ??
        payload['org_id'] ??
        payload['organization']) as string | undefined;
      if (orgId) sessionStorage.setItem('orgId', orgId);
    }
  }
}
